import ReactGA from 'react-ga4';
import TagManager from 'react-gtm-module';
import { GA_CONFIG, GA_EVENTS, GA_DIMENSIONS, isInDevelopment } from './constants';
import { logError, gaEventGuard } from './utils';
import { DATA_LAYER_SESSION_KEYS, GTM_GENERIC_TAG_KEYS } from './gtmWhitelist';
import { FORWARDED_EVENT_PARAMS } from './eventParams';
import { FUND_FIELDS, isEmptyValue, PORTFOLIO_FIELDS } from '../utils/constants/fields';

let isInit = false;

// Current user id (Cognito username) — set when the login event fires, cleared on logout.
// pushEvent auto-attaches it to every event so GA4 can stitch a user across devices.
let currentUserId = '';

// Readable company type of the signed-in user's company (e.g. 'Private Equity'), set once the
// company profile and its options are loaded, cleared on logout. pushEvent attaches it to every
// event, like the user id, so GA4 can report usage per customer type for existing customers too.
let currentCompanyType = '';

// Timestamp (ms) of this session's login, used to compute logout session duration.
let sessionStart = null;

/**
 * Returns the user id (Cognito username) currently attached to analytics events.
 * @returns {string}
 */
export const getCurrentUserId = () => currentUserId;

/**
 * Initializes GTM and GA4
 */
export const initGA = () => {
  if (!GA_CONFIG.gaMeasurementId && !GA_CONFIG.gtmContainerId) {
    return;
  }

  const isInternalTraffic = isInDevelopment;

  const initOptions = {
    ...(isInDevelopment ? { [GA_DIMENSIONS.debug_mode]: true } : {}),
    ...(isInternalTraffic ? { [GA_DIMENSIONS.traffic_type]: 'internal' } : {})
  };

  const gaOptions = {
    anonymizeIp: true,
    siteSpeedSampleRate: 100,
    ...initOptions
  };

  if (GA_CONFIG.gaMeasurementId) {
    ReactGA.initialize(GA_CONFIG.gaMeasurementId, {
      [GA_DIMENSIONS.testMode]: isInDevelopment,
      gaOptions
      // legacyDimensionMetric: false, // uncomment if needed to be false
    });
  }

  if (GA_CONFIG.gtmContainerId) {
    TagManager.initialize({
      gtmId: GA_CONFIG.gtmContainerId,
      dataLayer: {
        anonymizeIp: true,
        ...initOptions
      }
    });

    isInit = true;
  }
};

/**
 * Pushes event objects to GTM data layer.
 * @param {Object} mainEventData - The event object.
 */
const pushEvent = (eventName = '', mainEventData = {}) => {
  if (!isInit) {
    return;
  }

  if (!Object.values(GA_EVENTS).includes(eventName)) {
    logError(`[pushEvent] 'eventName' param (${eventName}) is wrong!`);
  }

  // Events built by the caller have their keys declared, so the GTM field contract test covers
  // them too. An undeclared key would bypass that test, so flag it in DEV/STG.
  const declaredParams = FORWARDED_EVENT_PARAMS[eventName];
  const undeclaredParams = declaredParams
    ? Object.keys(mainEventData).filter((key) => !declaredParams.includes(key))
    : [];
  if (undeclaredParams.length) {
    logError(
      `[pushEvent] '${eventName}' carries undeclared params (${undeclaredParams.join(', ')}); ` +
        'add them to FORWARDED_EVENT_PARAMS in src/ga4/eventParams.js.'
    );
  }

  const dataLayerVariables = {};
  if (isInDevelopment) {
    Object.assign(
      dataLayerVariables,
      Object.fromEntries(
        [GA_DIMENSIONS.testMode, GA_DIMENSIONS.debug_mode, GA_DIMENSIONS.debug_event].map((dim) => [
          dim,
          true
        ])
      )
    );
  }

  // if traffic type is internal dataLayerVariable.traffic_type = 'internal'

  const eventData = { ...dataLayerVariables, ...mainEventData, event: eventName };

  // Attach the current user_id to every event (cross-device stitching), unless the
  // caller already set one explicitly (e.g. the login event).
  if (eventData[GA_DIMENSIONS.user_id] === undefined && currentUserId) {
    eventData[GA_DIMENSIONS.user_id] = currentUserId;
  }
  if (eventData[GA_DIMENSIONS.user_company_type] === undefined && currentCompanyType) {
    eventData[GA_DIMENSIONS.user_company_type] = currentCompanyType;
  }

  if (isInDevelopment) {
    const styles = {
      header: 'color: #10b981; font-weight: bold; font-size: 14px;',
      section: 'color: #06b6d4; font-weight: bold;',
      label: 'color: #f59e0b; font-weight: bold;',
      event: 'color: #ec4899; font-weight: bold;',
      muted: 'color: #9ca3af;'
    };

    console.log('%c📊 DEV/STG Logs - Google Analytics Event [ START ]', styles.header);

    console.log('%c🎯 Event Details:', styles.section);
    console.log('%cEvent Name:', styles.label, eventData.event);

    console.log('%c📋 Complete Event Data:', styles.section);
    console.log(eventData);

    console.log('%c✅ DEV/STG Logs - Google Analytics Event [  END  ]', styles.header);
    console.log('');
  }

  // GTM merges every push into one persistent data model, so a key pushed here would ride along
  // on every later event until something overwrote it. Unset this event's own keys once GTM has
  // processed the event. Built before the push, because GTM annotates the messages it receives.
  //
  // Deliberately not `dataLayer.push(function () { this.reset(); })`: most dedicated tags in the
  // container are gated on `ucCategory.functional`, a consent key that Usercentrics pushes and this
  // app never does, so a reset would silence those tags for the rest of the session. The session
  // keys (user id, company type) stay as well: tags on GTM's own events (history change,
  // consent_status) read them from the model, and eventLogout unsets them.
  const unsetEventData = Object.fromEntries(
    Object.keys(eventData)
      .filter((key) => key !== 'event' && !DATA_LAYER_SESSION_KEYS.includes(key))
      .map((key) => [key, undefined])
  );

  // Other scripts write to the same data model, and the generic tag cannot tell whose key it
  // reads: Usercentrics' consent_status carries `action` ('onInitialPageLoad', ...), which the tag
  // would otherwise send with every later event that has no `action` of its own. So, before the
  // event, clear every key the generic tag reads that this event does not set (session keys
  // aside). The consent flags the dedicated tags need are not generic-tag keys and stay.
  const unsetForeignData = Object.fromEntries(
    GTM_GENERIC_TAG_KEYS.filter(
      (key) => !(key in eventData) && !DATA_LAYER_SESSION_KEYS.includes(key)
    ).map((key) => [key, undefined])
  );

  window.dataLayer.push(unsetForeignData);
  window.dataLayer.push(eventData);
  if (Object.keys(unsetEventData).length) {
    window.dataLayer.push(unsetEventData);
  }
};

/**
 * Generic event tracker. Pushes a registered event with arbitrary (non-PII) params.
 * `user_id` is attached automatically by pushEvent. The event name must exist in
 * GA_EVENTS (pushEvent logs an error otherwise).
 * @param {string} eventName - A value from GA_EVENTS.
 * @param {Object} [params] - Additional non-PII event parameters.
 */
export const track = (eventName = '', params = {}) => {
  gaEventGuard(() => {
    pushEvent(eventName, params);
  });
};

/**
 * Sets the company type attached to every later event as `user_company_type`; an empty value
 * clears it. Also written to the data model at once, so tags on GTM's own events see it before
 * the next app event.
 * @param {string} [companyType] - Readable company type, e.g. 'Private Equity'.
 */
export const setAnalyticsCompanyType = (companyType = '') => {
  gaEventGuard(() => {
    const next = companyType === null || companyType === undefined ? '' : String(companyType);
    if (next === currentCompanyType) {
      return;
    }
    currentCompanyType = next;
    if (isInit) {
      window.dataLayer.push({ [GA_DIMENSIONS.user_company_type]: next || undefined });
    }
  });
};

export const eventSignUpStep1 = (companyType = '') => {
  gaEventGuard(() => {
    const signUpStep1EventData = { [GA_DIMENSIONS.cType]: companyType };
    pushEvent(GA_EVENTS.sign_up_step_1, signUpStep1EventData);
  });
};

export const eventSignUpStep2 = (companyType = '') => {
  gaEventGuard(() => {
    const signUpStep2EventData = { [GA_DIMENSIONS.cType]: companyType };
    pushEvent(GA_EVENTS.sign_up_step_2, signUpStep2EventData);
  });
};

export const eventLogin = (userId = '', method = 'email') => {
  gaEventGuard(() => {
    currentUserId = userId || '';
    if (!sessionStart) {
      sessionStart = Date.now();
    }
    const loginEventData = { [GA_DIMENSIONS.method]: method, [GA_DIMENSIONS.user_id]: userId };
    pushEvent(GA_EVENTS.login, loginEventData);
  });
};

export const eventPageviewStartPage = (fundsCount = 0, portfoliosCount = 0) => {
  gaEventGuard(() => {
    if (typeof fundsCount !== 'number') {
      logError(
        `[eventPageviewStartPage] 'fundsCount' param (${fundsCount}) must be of type number (${typeof fundsCount})!`
      );
    }

    if (typeof portfoliosCount !== 'number') {
      logError(
        `[eventPageviewStartPage] 'portfoliosCount' param (${portfoliosCount}) must be of type number (${typeof portfoliosCount})!`
      );
    }

    const pageviewStartPageEventData = {
      [GA_DIMENSIONS.funds]: fundsCount.toString(),
      [GA_DIMENSIONS.portfolio_companies]: portfoliosCount.toString()
    };

    pushEvent(GA_EVENTS.pageview_start_page, pageviewStartPageEventData);
  });
};

export const eventPageviewFundCreate = () => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.pageview_fund_create);
  });
};

export const eventFundCreationStep = (event = '', fundData = {}) => {
  gaEventGuard(() => {
    if (
      ![
        GA_EVENTS.fund_create_Information_complete,
        GA_EVENTS.fund_create_GeoIndustries_complete,
        GA_EVENTS.fund_create_financials_complete,
        GA_EVENTS.fund_create_transaction_complete,
        GA_EVENTS.fund_create_valuation_complete,
        GA_EVENTS.fund_create_complete
      ].includes(event)
    ) {
      logError(`[eventFundCreationStep] 'event' param (${event}) is wrong!`);
    }

    const fundCreationStepEventData = Object.fromEntries(
      Object.values(FUND_FIELDS)
        .filter((fieldConfig) => !!fieldConfig.GA4?.[event])
        .map((fieldConfig) => {
          const gaFieldName = Object.entries(fieldConfig.GA4).filter(
            ([eventName]) => eventName === event
          )[0][1];

          const fieldValue = fundData[fieldConfig.NAME];

          const gaFieldValue = isEmptyValue(fieldValue)
            ? ''
            : Array.isArray(fieldValue)
              ? fieldValue.join(' - ')
              : fieldValue.toString();

          return [gaFieldName, gaFieldValue];
        })
    );

    pushEvent(event, fundCreationStepEventData);
  });
};

export const eventCreateCompanyProfilePageView = () => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.company_create_page_view);
  });
};

export const eventCompleteCompanyProfile = (companyType = '', country = '', tagline = '') => {
  gaEventGuard(() => {
    const completeCompanyProfileEventData = {
      [GA_DIMENSIONS.company_type]: (companyType || '').toString(),
      // The container's complete_company_profile tag reads cType, the key the sign-up events use
      // for this value, and no tag reads company_type on this event. Keep cType.
      [GA_DIMENSIONS.cType]: (companyType || '').toString(),
      [GA_DIMENSIONS.Country]: (country || '').toString(),
      [GA_DIMENSIONS.Tagline]: (tagline || '').toString()
    };

    pushEvent(GA_EVENTS.complete_company_profile, completeCompanyProfileEventData);
  });
};

export const eventPageviewPortfolioCoCreate = () => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.pageview_portfolio_co_create);
  });
};

export const eventPortfolioCoCreationStep = (event = '', fundData = {}) => {
  gaEventGuard(() => {
    if (
      ![
        GA_EVENTS.portfolio_co_information_complete,
        GA_EVENTS.portfolio_co_geoindustries_complete,
        GA_EVENTS.portfolio_co_financials_complete,
        GA_EVENTS.portfolio_co_transaction_complete,
        GA_EVENTS.portfolio_co_valuation_complete,
        GA_EVENTS.portfolio_co_create_complete
      ].includes(event)
    ) {
      logError(`[eventFundCreationStep] 'event' param (${event}) is wrong!`);
    }

    // A step event carries its own tab's fields. The create-complete tag reports the whole
    // portfolio company, so that event carries every mapped field from the final form.
    const isCreateComplete = event === GA_EVENTS.portfolio_co_create_complete;

    const portfolioCoCreationStepEventData = Object.fromEntries(
      Object.values(PORTFOLIO_FIELDS)
        .filter((fieldConfig) =>
          isCreateComplete ? !!fieldConfig.GA4 : !!fieldConfig.GA4?.[event]
        )
        .map((fieldConfig) => {
          const gaFieldName = fieldConfig.GA4[event] ?? Object.values(fieldConfig.GA4)[0];

          const fieldValue = fundData[fieldConfig.NAME];

          const gaFieldValue = isEmptyValue(fieldValue)
            ? ''
            : Array.isArray(fieldValue)
              ? fieldValue.join(' - ')
              : fieldValue.toString();

          return [gaFieldName, gaFieldValue];
        })
    );

    pushEvent(event, portfolioCoCreationStepEventData);
  });
};

export const eventPageviewInvestProfileCreate = () => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.pageview_invest_profile_create);
  });
};

export const eventDownloadSearch = () => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.download_search);
  });
};

export const eventOutreachSearch = () => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.outreach_search);
  });
};

// --- Auth flow ---

export const eventSignUpSubmitted = (companyType = '', hasPhone = false) => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.sign_up_submitted, {
      signup_method: 'email',
      company_type: (companyType || '').toString(),
      has_phone: !!hasPhone
    });
  });
};

export const eventSignUpCompleted = (companyType = '', hasPhone = false) => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.sign_up_completed, {
      signup_method: 'email',
      company_type: (companyType || '').toString(),
      has_phone: !!hasPhone
    });
  });
};

export const eventSignUpFailed = (errorType = 'server_error', errorField = null) => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.sign_up_failed, {
      error_type: errorType,
      error_field: errorField || null
    });
  });
};

export const eventLoginFailed = (reason = '') => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.login_failed, { reason });
  });
};

export const eventLogout = () => {
  gaEventGuard(() => {
    const session_duration_seconds = sessionStart
      ? Math.floor((Date.now() - sessionStart) / 1000)
      : null;
    pushEvent(GA_EVENTS.logout, { session_duration_seconds });
    sessionStart = null;
    currentUserId = '';
    currentCompanyType = '';
    // pushEvent keeps the session keys in the data model between events; drop them so that
    // nothing sent after logout is attributed to this user.
    if (isInit) {
      window.dataLayer.push(
        Object.fromEntries(DATA_LAYER_SESSION_KEYS.map((key) => [key, undefined]))
      );
    }
  });
};

export const eventPasswordResetRequested = () => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.password_reset_requested);
  });
};

// --- Onboarding / activation funnel ---

export const eventOnboardingStepPending = (stepName = '') => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.onboarding_step_pending, { step_name: stepName });
  });
};

export const eventOnboardingCompanyProfileCompleted = () => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.onboarding_company_profile_completed);
  });
};

export const eventOnboardingUserAccountCompleted = () => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.onboarding_user_account_completed);
  });
};

export const eventActivation = (companyType = '') => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.activation, { company_type: (companyType || '').toString() });
  });
};

// --- Search / Profiler ---

export const eventSearchTabSwitched = (fromTab = '', toTab = '') => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.search_tab_switched, { from_tab: fromTab, to_tab: toTab });
  });
};

export const eventSearchTypeChanged = (searchProfile = '', fromType = '', toType = '') => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.search_type_changed, {
      search_profile: searchProfile,
      from_type: fromType,
      to_type: toType
    });
  });
};

export const eventSearchAdvancedExpanded = (searchProfile = '', searchType = '') => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.search_advanced_expanded, {
      search_profile: searchProfile,
      search_type: searchType
    });
  });
};

// params is a pre-built non-PII object (see ga4/searchEventParams.js).
export const eventSearchExecuted = (params = {}) => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.search_executed, params);
  });
};

export const eventSearchResultsDisplayed = (params = {}) => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.search_results_displayed, params);
  });
};

export const eventSearchResultClicked = (params = {}) => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.search_result_clicked, params);
  });
};

// --- Company Detail ---

export const eventCompanyProfileViewed = (companyId = '', refSource = 'direct', searchId = null) => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.company_profile_viewed, {
      company_id: companyId,
      ref_source: refSource,
      search_id: searchId || null
    });
  });
};

export const eventCompanyTabChanged = (companyId = '', toTab = '') => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.company_tab_changed, { company_id: companyId, to_tab: toTab });
  });
};

export const eventCompanyActionClicked = (companyId = '', action = '') => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.company_action_clicked, { company_id: companyId, action });
  });
};

// --- Watchlist ---

export const eventWatchlistViewed = (itemCount = 0) => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.watchlist_viewed, { item_count: itemCount });
  });
};

export const eventWatchlistItemAdded = (companyId = '', source = '') => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.watchlist_item_added, { company_id: companyId, source });
  });
};

export const eventWatchlistItemRemoved = (companyId = '') => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.watchlist_item_removed, { company_id: companyId });
  });
};

export const eventWatchlistCheckUpdatesClicked = (companyId = '') => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.watchlist_check_updates_clicked, { company_id: companyId });
  });
};

// --- Projects ---

export const eventProjectsViewed = (savedCount = 0, archivedCount = 0) => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.projects_viewed, { saved_count: savedCount, archived_count: archivedCount });
  });
};

export const eventProjectTabChanged = (toTab = '') => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.project_tab_changed, { to_tab: toTab });
  });
};

export const eventProjectViewDetailsClicked = (projectId = '') => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.project_view_details_clicked, { project_id: projectId });
  });
};

export const eventProjectReExecuted = (projectId = '') => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.project_re_executed, { project_id: projectId });
  });
};

export const eventProjectArchived = (projectId = '') => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.project_archived, { project_id: projectId });
  });
};

export const eventProjectRestored = (projectId = '') => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.project_restored, { project_id: projectId });
  });
};

export const eventProjectDeleted = (projectId = '') => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.project_deleted, { project_id: projectId });
  });
};

export const eventProjectCreated = (projectId = '', searchReason = '') => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.project_created, { project_id: projectId, search_reason: searchReason });
  });
};

// --- Agentic AI ---

export const eventAgenticAiOpened = () => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.agentic_ai_opened);
  });
};

export const eventAgenticAiQuickActionClicked = (action = '') => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.agentic_ai_quick_action_clicked, { action });
  });
};

export const eventAgenticAiMessageSent = (
  messageLength = 0,
  inputMethod = 'typed',
  conversationId = null
) => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.agentic_ai_message_sent, {
      message_length: messageLength,
      input_method: inputMethod,
      conversation_id: conversationId
    });
  });
};

export const eventAgenticAiResponseReceived = (
  conversationId = null,
  responseDurationMs = null
) => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.agentic_ai_response_received, {
      conversation_id: conversationId,
      response_duration_ms: responseDurationMs
    });
  });
};

export const eventAgenticAiNewChat = (conversationId = null) => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.agentic_ai_new_chat, { conversation_id: conversationId });
  });
};

export const eventAgenticAiConversationOpened = (conversationId = null) => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.agentic_ai_conversation_opened, { conversation_id: conversationId });
  });
};

export const eventAgenticAiConversationFavorited = (conversationId = null) => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.agentic_ai_conversation_favorited, { conversation_id: conversationId });
  });
};

export const eventAgenticAiConversationArchived = (conversationId = null) => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.agentic_ai_conversation_archived, { conversation_id: conversationId });
  });
};

export const eventAgenticAiQuotaExhausted = () => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.agentic_ai_quota_exhausted, { tokens_remaining_at_block: 0 });
  });
};

// --- Team ---

export const eventTeamPageViewed = (openRequestCount = 0, resolvedRequestCount = 0) => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.team_page_viewed, {
      open_request_count: openRequestCount,
      resolved_request_count: resolvedRequestCount
    });
  });
};

export const eventTeamTabChanged = (toTab = '') => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.team_tab_changed, { to_tab: toTab });
  });
};

export const eventTeamRequestShared = (requestId = '', recipientCount = 0) => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.team_request_shared, {
      request_id: requestId,
      recipient_count: recipientCount
    });
  });
};

export const eventTeamFeedbackSubmitted = (requestId = '', feedbackLength = 0) => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.team_feedback_submitted, {
      request_id: requestId,
      feedback_length: feedbackLength
    });
  });
};

export const eventTeamRequestResolved = (requestId = '') => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.team_request_resolved, { request_id: requestId });
  });
};

// --- Outreach ---

export const eventOutreachStarted = (projectId = '', recipientCount = 0, messageLength = 0) => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.outreach_started, {
      project_id: projectId,
      recipient_count: recipientCount,
      message_length: messageLength
    });
  });
};

export const eventOutreachMessageSent = (outreachId = '', messageLength = 0) => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.outreach_message_sent, {
      outreach_id: outreachId,
      message_length: messageLength
    });
  });
};

// Agent message controls (TD-2345 / TD-2346 / TD-2347)
export const eventMessageCopyUserMessage = () => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.message_copy_user_message);
  });
};

export const eventMessageRerunClicked = (sourceMessageId = '') => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.message_rerun_clicked, { source_message_id: sourceMessageId });
  });
};

export const eventMessageStopClicked = () => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.message_stop_clicked);
  });
};

// --- Agent contact enrichment (TD-2293) ---
// Metadata only — kind, counts, cost, status. NEVER pass revealed Work Email /
// Mobile Phone values or contact names/ids into GA4 (G5).

export const eventAgentEnrichmentOptionsShown = (kind = '', optionCount = 0) => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.agent_enrichment_options_shown, { kind, option_count: optionCount });
  });
};

export const eventAgentEnrichmentOptionSelected = (kind = '') => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.agent_enrichment_option_selected, { kind });
  });
};

export const eventAgentEnrichmentRevealConfirmShown = (cost = 0) => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.agent_enrichment_reveal_confirm_shown, { cost });
  });
};

export const eventAgentEnrichmentRevealConfirmed = (cost = 0) => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.agent_enrichment_reveal_confirmed, { cost });
  });
};

export const eventAgentEnrichmentRevealCancelled = () => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.agent_enrichment_reveal_cancelled);
  });
};

export const eventAgentEnrichmentRevealJobStarted = () => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.agent_enrichment_reveal_job_started);
  });
};

export const eventAgentEnrichmentRevealJobCompleted = (
  status = '',
  enrichedCount = 0,
  totalContacts = 0
) => {
  gaEventGuard(() => {
    pushEvent(GA_EVENTS.agent_enrichment_reveal_job_completed, {
      status,
      enriched_count: enrichedCount,
      total_contacts: totalContacts
    });
  });
};
